/**
 * Engagement Insights — a read-only, org-wide rollup across Announcements (ADR 0029) and Surveys
 * (ADR 0027): aggregate read/acknowledgement/response rates, the eNPS trend over every eNPS-bearing
 * survey run so far, the latest eNPS cycle's department participation (reusing that survey's own
 * anonymity-threshold slices), which live mandatory announcements still have people pending, and a
 * blended Engagement Index, a department heatmap, driver scores and keyword themes.
 *
 * No new write path and no new data: every number here is derived from rows `announcement.ts` and
 * `survey.ts` already compute, aggregated across them. Survey results go through `results()` and so
 * keep its anonymity threshold; nothing here re-derives it. Themes are rule-based keyword counts, not
 * an AI model. Permission is any-of `announcement:manage` / `survey:manage`.
 *
 * Filters: `from`/`to` (inclusive, date-only) window the announcements by publish day and the
 * surveys by whether they were open during the window; `departmentId` narrows the rates and the
 * survey-derived sections to one department; `scope: 'team'` narrows the announcement figures to the
 * caller's direct reports. Anonymous survey answers cannot be attributed to a team, so team scope
 * reports `surveyDataAvailable: false` and leaves the survey-derived sections empty rather than
 * showing org-wide numbers under a team heading.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { dateOnlySchema, errorBodySchema, instantSchema, uuidSchema } from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

export const engagementScopeSchema = z.enum(['org', 'team']);
export type EngagementScope = z.infer<typeof engagementScopeSchema>;

export const engagementInsightsQuerySchema = z.object({
  /** First day of the window, inclusive. */
  from: dateOnlySchema.optional(),
  /** Last day of the window, inclusive. */
  to: dateOnlySchema.optional(),
  /** Narrow the rates and survey-derived sections to one department. */
  departmentId: uuidSchema.optional(),
  /** `org` (default) is the whole organisation. `team` is the caller's direct reports. */
  scope: engagementScopeSchema.optional(),
});
export type EngagementInsightsQuery = z.infer<typeof engagementInsightsQuerySchema>;

export const engagementRateSchema = z.object({
  /** Null when the denominator is zero — nothing to measure yet, not a 0%. */
  rate: z.number().nullable(),
  numerator: z.number().int().nonnegative(),
  denominator: z.number().int().nonnegative(),
});
export type EngagementRate = z.infer<typeof engagementRateSchema>;

export const enpsPointSchema = z.object({
  surveyId: uuidSchema,
  /** Whatever HR named the survey — no invented "Q1 2026" label when the org's own fiscal/survey
   *  calendar isn't something this module tracks (ADR: Engage is org-scoped, not legal-entity
   *  scoped). */
  periodLabel: z.string(),
  score: z.number(),
  promoters: z.number().int().nonnegative(),
  passives: z.number().int().nonnegative(),
  detractors: z.number().int().nonnegative(),
  respondents: z.number().int().nonnegative(),
  closedAt: instantSchema.nullable(),
});
export type EnpsPoint = z.infer<typeof enpsPointSchema>;

export const departmentParticipationSchema = z.object({
  departmentId: uuidSchema.nullable(),
  departmentName: z.string(),
  headcount: z.number().int().nonnegative(),
  /** Null when `belowThreshold` — the anonymity floor, never a real count underneath it. */
  respondents: z.number().int().nonnegative().nullable(),
  participationRate: z.number().nullable(),
  belowThreshold: z.boolean(),
});
export type DepartmentParticipation = z.infer<typeof departmentParticipationSchema>;

export const nonAcknowledgerRowSchema = z.object({
  announcementId: uuidSchema,
  title: z.string(),
  audienceSize: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  acknowledged: z.number().int().nonnegative(),
  pending: z.number().int().positive(),
  deadline: instantSchema.nullable(),
});
export type NonAcknowledgerRow = z.infer<typeof nonAcknowledgerRowSchema>;

/**
 * The blended Engagement Index (packages/shared's engagement-index.ts). `value` is 0-100, or null
 * when no part has data. Each part is shown beside it so the number can be explained.
 */
export const engagementIndexPartsSchema = z.object({
  /** 0 to 1, or null when no mandatory acknowledgement has been requested yet. */
  acknowledgementRate: z.number().nullable(),
  /** 0 to 1, or null when no survey has an audience yet. */
  surveyResponseRate: z.number().nullable(),
  /** -100 to 100, or null when no eNPS survey has closed. */
  enps: z.number().nullable(),
});
export type EngagementIndexParts = z.infer<typeof engagementIndexPartsSchema>;

export const departmentOptionSchema = z.object({
  id: uuidSchema,
  name: z.string(),
});
export type DepartmentOption = z.infer<typeof departmentOptionSchema>;

/** One heatmap column: a rating question, matched across surveys by its prompt. */
export const heatmapColumnSchema = z.object({
  key: z.string(),
  prompt: z.string(),
});
export type HeatmapColumn = z.infer<typeof heatmapColumnSchema>;

export const heatmapCellSchema = z.object({
  key: z.string(),
  /** Mean rating (1 to 5) across the released surveys that cover this cell. Null when suppressed. */
  average: z.number().nullable(),
  /** Answers behind the average. Null when suppressed. */
  responses: z.number().int().nonnegative().nullable(),
  /** True when no contributing slice met the anonymity threshold, so nothing is shown. */
  belowThreshold: z.boolean(),
});
export type HeatmapCell = z.infer<typeof heatmapCellSchema>;

export const departmentHeatmapRowSchema = z.object({
  departmentId: uuidSchema,
  departmentName: z.string(),
  /** One cell per column, in column order. */
  cells: z.array(heatmapCellSchema),
});
export type DepartmentHeatmapRow = z.infer<typeof departmentHeatmapRowSchema>;

export const departmentHeatmapSchema = z.object({
  minimumResponses: z.number().int().positive(),
  columns: z.array(heatmapColumnSchema),
  rows: z.array(departmentHeatmapRowSchema),
});
export type DepartmentHeatmap = z.infer<typeof departmentHeatmapSchema>;

/** Driver score for one rating question (1 to 5), matched across surveys by its prompt. */
export const driverScoreSchema = z.object({
  key: z.string(),
  prompt: z.string(),
  /** Answers behind the figures. Null when suppressed. */
  responses: z.number().int().nonnegative().nullable(),
  /** Mean rating (1 to 5). Null when suppressed. */
  average: z.number().nullable(),
  /** Share of answers scoring 4 or 5, 0 to 1. Null when suppressed. */
  favourableShare: z.number().nullable(),
  belowThreshold: z.boolean(),
});
export type DriverScore = z.infer<typeof driverScoreSchema>;

export const themeKeywordSchema = z.object({
  keyword: z.string(),
  /** Number of free-text answers that mention the keyword. */
  count: z.number().int().positive(),
});
export type ThemeKeyword = z.infer<typeof themeKeywordSchema>;

/**
 * Keyword themes from free-text survey answers. Rule-based (word frequency with a stop-word list),
 * never an AI model: the UI must say so.
 */
export const engagementThemesSchema = z.object({
  method: z.literal('keyword_frequency'),
  /** Keywords named by fewer answers than this are not shown, and neither are themes at all when
   *  there are fewer free-text answers than this. Same floor as the survey anonymity threshold. */
  minimumResponses: z.number().int().positive(),
  /** Free-text answers analysed. Null when suppressed. */
  answersAnalysed: z.number().int().nonnegative().nullable(),
  belowThreshold: z.boolean(),
  items: z.array(themeKeywordSchema),
});
export type EngagementThemes = z.infer<typeof engagementThemesSchema>;

/** The manager's view of their direct reports (scope `team` only). */
export const managerViewSchema = z.object({
  directReportCount: z.number().int().nonnegative(),
  /** True when the team is smaller than the anonymity threshold: the team's rates are withheld. */
  belowThreshold: z.boolean(),
  /** Null when `belowThreshold`. */
  announcementReadRate: engagementRateSchema.nullable(),
  /** Null when `belowThreshold`. */
  announcementAckRate: engagementRateSchema.nullable(),
});
export type ManagerView = z.infer<typeof managerViewSchema>;

export const engagementInsightsSchema = z.object({
  /** The scope and filters this response was computed for. */
  scope: engagementScopeSchema,
  appliedFrom: dateOnlySchema.nullable(),
  appliedTo: dateOnlySchema.nullable(),
  appliedDepartmentId: uuidSchema.nullable(),
  /** False when team scope is active: survey answers are anonymous and cannot be attributed to a
   *  team, so the survey-derived sections below are empty. */
  surveyDataAvailable: z.boolean(),
  /** Every department in the organisation, for the filter. */
  departmentOptions: z.array(departmentOptionSchema),

  announcementReadRate: engagementRateSchema,
  announcementAckRate: engagementRateSchema,
  surveyResponseRate: engagementRateSchema,

  /** Blend of acknowledgement rate, survey response rate and eNPS, 0 to 100. Null when no data, and
   *  null under team scope (the blend is org-wide). */
  engagementIndex: z.number().int().min(0).max(100).nullable(),
  engagementIndexParts: engagementIndexPartsSchema,

  /** The most recently closed (or, if none closed yet, the most recently opened) eNPS survey. */
  currentEnps: enpsPointSchema.nullable(),
  /** Every CLOSED eNPS survey in the window, oldest first. There is no recurrence feature yet (ADR),
   *  so this is only as long as HR's own manually-run eNPS cycles. */
  enpsTrend: z.array(enpsPointSchema),
  /** The current eNPS survey's department breakdown, reusing its own N-response anonymity floor. */
  departmentParticipation: z.array(departmentParticipationSchema),

  /** Rating questions by department, averaged across the released surveys in the window. */
  departmentHeatmap: departmentHeatmapSchema,
  /** Per rating question across the released surveys in the window (or the chosen department). */
  driverScores: z.array(driverScoreSchema),
  /** Keyword themes from the free-text answers of the released surveys in the window. */
  themes: engagementThemesSchema,

  /** The caller's direct reports. Present only under team scope. */
  managerView: managerViewSchema.nullable(),

  /** Live announcements that require acknowledgement and still have at least one person pending. */
  nonAcknowledgers: z.array(nonAcknowledgerRowSchema),
});
export type EngagementInsights = z.infer<typeof engagementInsightsSchema>;

export const engagementInsightsContract = c.router(
  {
    getEngagementInsights: {
      method: 'GET',
      path: '/',
      query: engagementInsightsQuerySchema,
      responses: {
        200: engagementInsightsSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Org-wide Engage analytics: rates, Engagement Index, eNPS trend, department heatmap, driver scores, keyword themes, team view',
      metadata: routeMeta(['announcement:manage', 'survey:manage']),
    },
  },
  { pathPrefix: '/engagement-insights' },
);
